import { EMPTY_INPUT, type PlayerInput } from '../sim/input';
import { z } from 'zod';
import { LevelSchema, type LevelDef } from '../sim/level/schema';
import type { MatchSettings } from '../sim/rules/settings';
import { ARMS_RACE_LADDER, type ArmsRaceProgress } from '../sim/rules/armsRaceConfig';
import type { WorldSnapshot } from '../sim/snapshot';
import type { KillStreakProgress } from '../sim/rules/killStreaks';
import { MAX_MESSAGE_CHARS } from './relay';
import { MAX_PLAYERS } from '../core/players';
import { ACCESSORIES, MARKINGS, PERSONALITIES, SKINS, type CharacterStyle } from '../core/characters';

/**
 * Game messages exchanged through the room relay (`src/net/room.ts`).
 *
 * Online play is a host-authoritative mirror: every peer runs the same deterministic sim; clients
 * send their `PlayerInput`, the host steps the match and broadcasts the exact inputs it stepped each
 * tick (`frames`), and clients step through those. Nothing but inputs crosses the wire during play.
 */

/** `[moveX, aimX, aimY, buttons]`, analog axes as integers in 1/1000ths (see {@link packInput}). */
export type PackedInput = [number, number, number, number];

/** Who stands in each fighter slot of an online match; `peer: null` is a bot. */
export type MatchSlot = { peer: number | null; name: string; color: number; character?: CharacterStyle };

/** The rules every peer must create its sim with, so the mirrors agree. */
export type MatchRules = Pick<
  MatchSettings,
  'maxHp' | 'firstTo' | 'showWins' | 'rotation' | 'enabledWeapons' | 'enabledLevels'
> &
  PartyRules & { botDifficulty?: 'beginner' | 'standard' | 'expert'; roundTimeLimit?: number };

export type PartyRules = {
  mode?: 'classic' | 'teams' | 'king-of-hill' | 'weapon-roulette' | 'shared-stock' | 'arms-race' | 'office-brawl';
  mutators?: ('low-gravity' | 'giant-knockback' | 'melee-only' | 'same-weapon')[];
  teams?: number[];
  botStyles?: ('aggressive' | 'defensive' | 'opportunistic')[];
  modeScoreLimit?: number;
  teamStocks?: number;
  rouletteSeconds?: number;
};

export type LobbyPeerState = { id: number; name: string; slot: number; color: number; character?: CharacterStyle };

export const LOBBY_PRESET_IDS = ['classic', 'arms-race', 'office-brawl', 'teams', 'chaos', 'pirate', 'neon', 'custom'] as const;
export type LobbyPresetId = typeof LOBBY_PRESET_IDS[number];

/** A fresh deterministic round replaces a broken mirror; earned Arms Race stages also survive. */
export type RoundRecovery = {
  wins: number[];
  round: number;
  levelIndex: number;
  matchOver: boolean;
  winner: number;
  armsRace?: ArmsRaceProgress;
  killStreaks?: KillStreakProgress;
};

export const ROOM_PROTOCOL_VERSION = 9;

/** Host-owned readiness and rematch ballots. Epoch changes whenever the ballot's context changes. */
export type PartySnapshot = {
  t: 'party';
  epoch: number;
  revision: number;
  phase: 'lobby' | 'playing' | 'rematch';
  matchId: string | null;
  members: number[];
  ready: number[];
  votes: number[];
  arenaChoices?: string[];
  arenaVotes?: { peer: number; levelId: string }[];
};

export type NetMessage =
  | PartySnapshot
  /** A guest can only change its own ballot; the authenticated relay sender supplies its identity. */
  | { t: 'ready'; epoch: number; ready: boolean }
  | { t: 'rematch'; epoch: number; matchId: string; vote: boolean }
  | { t: 'arena-vote'; epoch: number; matchId: string; levelId: string }
  /** Client → host: change only the authenticated sender's public cosmetic appearance. */
  | { t: 'profile'; character: CharacterStyle }
  /** Host → all: the lobby as the host sees it, whenever it changes. */
  | {
      t: 'lobby';
      peers: LobbyPeerState[];
      maxHp: number;
      firstTo: number;
      bots: number;
      inMatch: boolean;
      partyRules?: PartyRules;
      rules?: MatchRules;
      presetId?: LobbyPresetId;
    }
  /** Host → all: create your sim exactly like this and start stepping when frames arrive. */
  | {
      t: 'start';
      version: typeof ROOM_PROTOCOL_VERSION;
      matchId: string;
      seed: number;
      levelId: string;
      slots: MatchSlot[];
      rules: MatchRules;
      extraLevels: LevelDef[];
      recovery?: RoundRecovery;
    }
  /** Client → host: my input right now (sent when it changes, at most once per tick). */
  | { t: 'input'; matchId: string; input: PackedInput }
  /** Host → all: the inputs stepped for ticks `start .. start + frames.length - 1`, plus a hash to check against. */
  | {
      t: 'frames';
      matchId: string;
      start: number;
      frames: PackedInput[][];
      hash?: { tick: number; h: string };
    }
  /** Host → all: the match is over; back to the lobby. */
  | { t: 'end'; reason: string }
  | { t: 'chat'; from: string; text: string }
  /** Client → host: restart this round for everybody, preserving the host's completed scores. */
  | { t: 'resync'; matchId: string; tick: number }
  /** Host → one client: a state snapshot to apply when the client reaches its tick. */
  | { t: 'snapshot'; snap: WorldSnapshot }
  | { t: 'hello'; name: string }
  | { t: 'event'; kind: string; payload: string }
  | { t: 'level'; json: string }
  | { t: 'settings'; json: string };

export function encode(msg: NetMessage): string {
  return JSON.stringify(msg);
}

export function decode(raw: string): NetMessage {
  return JSON.parse(raw) as NetMessage;
}

const integer = z.number().int().min(0).max(Number.MAX_SAFE_INTEGER);
const axis = z.number().int().min(-1000).max(1000);
export const packedInputSchema = z.tuple([axis, axis, axis, z.number().int().min(0).max(31)]);
const matchIdSchema = z.string().regex(/^[a-zA-Z0-9-]{1,64}$/);
const nameSchema = z.string().max(20);
const selectionSchema = z.union([z.literal('all'), z.array(z.string().min(1).max(128)).max(2048)]);
const characterSchema = z.object({
  personality: z.enum(PERSONALITIES),
  accessory: z.enum(ACCESSORIES),
  marking: z.enum(MARKINGS),
  skin: z.enum(SKINS).optional(),
}).strict();
const rulesSchema = z.object({
  maxHp: integer.min(1).max(99_999),
  firstTo: integer.max(999),
  showWins: z.boolean(),
  rotation: z.enum(['random', 'ordered']),
  enabledWeapons: selectionSchema,
  enabledLevels: selectionSchema,
  botDifficulty: z.enum(['beginner', 'standard', 'expert']).optional(),
  roundTimeLimit: integer.max(3600).optional(),
  mode: z
    .enum(['classic', 'teams', 'king-of-hill', 'weapon-roulette', 'shared-stock', 'arms-race', 'office-brawl'])
    .optional(),
  mutators: z
    .array(z.enum(['low-gravity', 'giant-knockback', 'melee-only', 'same-weapon']))
    .max(4)
    .refine((values) => new Set(values).size === values.length)
    .optional(),
  teams: z.array(z.number().int().min(0).max(1)).max(MAX_PLAYERS).optional(),
  botStyles: z
    .array(z.enum(['aggressive', 'defensive', 'opportunistic']))
    .max(MAX_PLAYERS)
    .optional(),
  modeScoreLimit: integer.min(5).max(120).optional(),
  teamStocks: integer.min(1).max(12).optional(),
  rouletteSeconds: integer.min(3).max(60).optional(),
});
const recoverySchema = z.object({
  wins: z.array(integer).length(MAX_PLAYERS),
  round: integer,
  levelIndex: integer,
  matchOver: z.boolean(),
  winner: z
    .number()
    .int()
    .min(-1)
    .max(MAX_PLAYERS - 1),
  killStreaks: z
    .array(
      z.object({ slot: integer.max(MAX_PLAYERS - 1), current: integer, best: integer })
        .strict()
        .refine((player) => player.current <= player.best),
    )
    .min(1)
    .max(MAX_PLAYERS)
    .refine((players) => new Set(players.map((player) => player.slot)).size === players.length)
    .optional(),
  armsRace: z
    .object({
      winner: z
        .number()
        .int()
        .min(-1)
        .max(MAX_PLAYERS - 1),
      players: z
        .array(
          z
            .object({
              slot: integer.max(MAX_PLAYERS - 1),
              stage: integer.max(ARMS_RACE_LADDER.length - 1),
              stageKills: integer.max(
                Math.max(...ARMS_RACE_LADDER.map((stage) => stage.killsRequired)),
              ),
              totalKills: integer.max(
                ARMS_RACE_LADDER.reduce((sum, stage) => sum + stage.killsRequired, 0),
              ),
            })
            .refine((player) => {
              const target = ARMS_RACE_LADDER[player.stage]?.killsRequired;
              return (
                target !== undefined &&
                player.stageKills <= target &&
                player.totalKills ===
                  ARMS_RACE_LADDER.slice(0, player.stage).reduce(
                    (sum, stage) => sum + stage.killsRequired,
                    0,
                  ) +
                    player.stageKills &&
                (player.stage === ARMS_RACE_LADDER.length - 1 || player.stageKills < target)
              );
            }),
        )
        .min(1)
        .max(MAX_PLAYERS),
    })
    .refine((race) => {
      if (new Set(race.players.map((player) => player.slot)).size !== race.players.length)
        return false;
      if (race.winner >= 0 && !race.players.some((player) => player.slot === race.winner))
        return false;
      return race.players.every((player) => {
        const finished =
          player.stage === ARMS_RACE_LADDER.length - 1 &&
          player.stageKills === ARMS_RACE_LADDER[player.stage]!.killsRequired;
        return finished === (player.slot === race.winner);
      });
    })
    .optional(),
});

/** Validate the actual room protocol at both relay and browser ingress; legacy simnet messages are not accepted. */
const roomMessageSchema = z.discriminatedUnion('t', [
  z.object({ t: z.literal('profile'), character: characterSchema }).strict(),
  z.object({ t: z.literal('arena-vote'), epoch: integer.min(1), matchId: matchIdSchema, levelId: z.string().min(1).max(128) }).strict(),
  z.object({ t: z.literal('ready'), epoch: integer.min(1), ready: z.boolean() }).strict(),
  z
    .object({
      t: z.literal('rematch'),
      epoch: integer.min(1),
      matchId: matchIdSchema,
      vote: z.boolean(),
    })
    .strict(),
  z
    .object({
      t: z.literal('party'),
      epoch: integer.min(1),
      revision: integer.min(1),
      phase: z.enum(['lobby', 'playing', 'rematch']),
      matchId: matchIdSchema.nullable(),
      members: z.array(integer.min(1)).min(1).max(MAX_PLAYERS),
      ready: z.array(integer.min(1)).max(MAX_PLAYERS),
      votes: z.array(integer.min(1)).max(MAX_PLAYERS),
      arenaChoices: z.array(z.string().min(1).max(128)).max(3).optional(),
      arenaVotes: z.array(z.object({ peer: integer.min(1), levelId: z.string().min(1).max(128) }).strict()).max(MAX_PLAYERS).optional(),
    })
    .strict(),
  z.object({ t: z.literal('input'), matchId: matchIdSchema, input: packedInputSchema }),
  z.object({ t: z.literal('resync'), matchId: matchIdSchema, tick: integer }),
  z.object({ t: z.literal('chat'), from: nameSchema, text: z.string().max(200) }),
  z.object({ t: z.literal('end'), reason: z.string().max(200) }),
  z.object({
    t: z.literal('lobby'),
    peers: z
      .array(
        z.object({
          id: integer.min(1),
          name: nameSchema,
          slot: integer.max(MAX_PLAYERS - 1),
          color: integer.max(MAX_PLAYERS - 1),
          character: characterSchema.optional(),
        }),
      )
      .max(MAX_PLAYERS),
    maxHp: integer.min(1).max(99_999),
    firstTo: integer.max(999),
    bots: integer.max(MAX_PLAYERS - 1),
    inMatch: z.boolean(),
    partyRules: rulesSchema
      .pick({
        mode: true,
        mutators: true,
        teams: true,
        botStyles: true,
        modeScoreLimit: true,
        teamStocks: true,
        rouletteSeconds: true,
      })
      .optional(),
    rules: rulesSchema.optional(),
    presetId: z.enum(LOBBY_PRESET_IDS).optional(),
  }),
  z.object({
    t: z.literal('start'),
    version: z.literal(ROOM_PROTOCOL_VERSION),
    matchId: matchIdSchema,
    seed: z.number().int().min(-2147483648).max(2147483647),
    levelId: z.string().min(1).max(128),
    slots: z
      .array(
        z.object({
          peer: integer.min(1).nullable(),
          name: nameSchema,
          color: integer.max(MAX_PLAYERS - 1),
          character: characterSchema.optional(),
        }),
      )
      .min(1)
      .max(MAX_PLAYERS),
    rules: rulesSchema,
    extraLevels: z.array(LevelSchema).max(128),
    recovery: recoverySchema.optional(),
  }),
  z.object({
    t: z.literal('frames'),
    matchId: matchIdSchema,
    start: integer,
    frames: z.array(z.array(packedInputSchema).min(1).max(MAX_PLAYERS)).min(1).max(120),
    hash: z.object({ tick: integer, h: z.string().regex(/^[a-f0-9]{8}$/) }).optional(),
  }),
]);

export type RoomGameMessage = Exclude<
  NetMessage,
  { t: 'snapshot' | 'hello' | 'event' | 'level' | 'settings' }
>;

export function readRoomMessage(
  raw: unknown,
  senderRole: 'host' | 'client',
): RoomGameMessage | null {
  if (typeof raw === 'string') {
    if (raw.length > MAX_MESSAGE_CHARS) return null;
    try {
      raw = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  const result = roomMessageSchema.safeParse(raw);
  if (!result.success) return null;
  const msg = result.data;
  if (senderRole === 'client' && !['input', 'chat', 'resync', 'ready', 'rematch', 'arena-vote', 'profile'].includes(msg.t))
    return null;
  if (senderRole === 'host' && ['input', 'resync', 'ready', 'rematch', 'arena-vote', 'profile'].includes(msg.t)) return null;
  if (msg.t === 'party') {
    const choices = msg.arenaChoices ?? [], ballots = msg.arenaVotes ?? [];
    if (new Set(choices).size !== choices.length || new Set(ballots.map((vote) => vote.peer)).size !== ballots.length) return null;
    if (ballots.some((vote) => !msg.members.includes(vote.peer) || !choices.includes(vote.levelId))) return null;
    if (msg.phase !== 'rematch' && (choices.length || ballots.length)) return null;
    if (msg.epoch > msg.revision || (msg.phase === 'lobby') !== (msg.matchId === null)) return null;
    if ([msg.members, msg.ready, msg.votes].some((ids) => new Set(ids).size !== ids.length))
      return null;
    if ([...msg.ready, ...msg.votes].some((id) => !msg.members.includes(id))) return null;
    if (
      (msg.phase !== 'lobby' && msg.ready.length) ||
      (msg.phase !== 'rematch' && msg.votes.length)
    )
      return null;
  }
  if (
    msg.t === 'lobby' &&
    (msg.peers.length + msg.bots > MAX_PLAYERS ||
      new Set(msg.peers.map((peer) => peer.id)).size !== msg.peers.length ||
      new Set(msg.peers.map((peer) => peer.slot)).size !== msg.peers.length)
  )
    return null;
  if (msg.t === 'lobby' && msg.rules) {
    if (msg.maxHp !== msg.rules.maxHp || msg.firstTo !== msg.rules.firstTo) return null;
    if (msg.partyRules && Object.entries(msg.partyRules).some(([key, value]) => {
      const fullValue = msg.rules![key as keyof MatchRules];
      return JSON.stringify(value) !== JSON.stringify(fullValue);
    })) return null;
  }
  if (msg.t === 'start') {
    // The sim assigns human slots before bot slots. Duplicate identities or interleaving change ownership.
    const peers = msg.slots.filter((s) => s.peer !== null).map((s) => s.peer);
    if (
      new Set(peers).size !== peers.length ||
      msg.slots.slice(0, peers.length).some((s) => s.peer === null)
    )
      return null;
    if (
      msg.recovery?.matchOver &&
      (msg.recovery.winner < 0 || msg.recovery.winner >= msg.slots.length)
    )
      return null;
    if (msg.recovery) {
      const streaks = msg.recovery.killStreaks;
      if (streaks && (streaks.length !== msg.slots.length || streaks.some((player) => player.slot >= msg.slots.length)))
        return null;
      const race = msg.recovery.armsRace;
      if ((msg.rules.mode === 'arms-race') !== !!race) return null;
      if (
        race &&
        (race.players.length !== msg.slots.length ||
          race.players.some((player) => player.slot >= msg.slots.length) ||
          race.winner !== msg.recovery.winner ||
          msg.recovery.matchOver !== race.winner >= 0)
      )
        return null;
    }
  }
  if (
    msg.t === 'frames' &&
    msg.hash &&
    (msg.hash.tick <= msg.start || msg.hash.tick > msg.start + msg.frames.length)
  )
    return null;
  if (msg.t === 'frames' && msg.frames.some((frame) => frame.length !== msg.frames[0]!.length))
    return null;
  return msg;
}

export function isPackedInput(raw: unknown): raw is PackedInput {
  return (
    Array.isArray(raw) &&
    raw.length === 4 &&
    raw.every(
      (v, i) =>
        typeof v === 'number' &&
        Number.isInteger(v) &&
        (i === 3 ? v >= 0 && v <= 31 : Math.abs(v) <= 1000),
    )
  );
}

/** Wire resolution of the analog axes. Direction error at 1/1000 is under a tenth of a degree. */
const AXIS_Q = 1000;
const B_JUMP = 1;
const B_DOWN = 2;
const B_ATTACK = 4;
const B_BLOCK = 8;
const B_THROW = 16;

function clamp(v: number, lo: number, hi: number): number {
  return Number.isFinite(v) ? Math.max(lo, Math.min(hi, v)) : 0;
}

/**
 * Quantize an input for the wire. The aim vector is normalized first (the sim normalizes anyway, so
 * only its direction matters) and every peer that unpacks the result gets bit-identical doubles.
 */
export function packInput(input: PlayerInput): PackedInput {
  let ax = clamp(input.aimX, -1e6, 1e6);
  let ay = clamp(input.aimY, -1e6, 1e6);
  const len = Math.hypot(ax, ay);
  if (len > 1e-6) {
    ax /= len;
    ay /= len;
  } else {
    ax = 1;
    ay = 0;
  }
  const bits =
    (input.jump ? B_JUMP : 0) |
    (input.down ? B_DOWN : 0) |
    (input.attack ? B_ATTACK : 0) |
    (input.block ? B_BLOCK : 0) |
    (input.throw ? B_THROW : 0);
  return [
    Math.round(clamp(input.moveX, -1, 1) * AXIS_Q),
    Math.round(ax * AXIS_Q),
    Math.round(ay * AXIS_Q),
    bits,
  ];
}

export function unpackInput(p: PackedInput): PlayerInput {
  if (!isPackedInput(p)) return { ...EMPTY_INPUT };
  const bits = p[3] | 0;
  return {
    moveX: (p[0] | 0) / AXIS_Q,
    aimX: (p[1] | 0) / AXIS_Q,
    aimY: (p[2] | 0) / AXIS_Q,
    jump: (bits & B_JUMP) !== 0,
    down: (bits & B_DOWN) !== 0,
    attack: (bits & B_ATTACK) !== 0,
    block: (bits & B_BLOCK) !== 0,
    throw: (bits & B_THROW) !== 0,
  };
}

/** The input the host actually steps with: what the wire can carry, so clients reproduce it exactly. */
export function wireInput(input: PlayerInput): PlayerInput {
  return unpackInput(packInput(input));
}

export function samePacked(a: PackedInput | null, b: PackedInput): boolean {
  return !!a && a[0] === b[0] && a[1] === b[1] && a[2] === b[2] && a[3] === b[3];
}

/** Latest analog state, but every button pressed in any of the inputs stays pressed: a tap survives. */
export function mergePresses(latest: PackedInput, earlier: PackedInput): PackedInput {
  return [latest[0], latest[1], latest[2], latest[3] | earlier[3]];
}

/** Quantized binary snapshot for the < 30 KB/s budget (20 Hz). */
export function encodeSnapshotBinary(snap: WorldSnapshot): Uint8Array {
  const n = snap.entities.length;
  const buf = new ArrayBuffer(16 + n * 16);
  const view = new DataView(buf);
  view.setUint32(0, snap.tick, true);
  view.setUint32(4, snap.rng, true);
  view.setUint16(8, snap.nextId, true);
  view.setUint16(10, n, true);
  let o = 16;
  for (const e of snap.entities) {
    view.setUint16(o, e.netId, true);
    const t = e.traits.Transform;
    view.setInt16(o + 2, Math.round(Number(t?.x ?? 0) * 100), true);
    view.setInt16(o + 4, Math.round(Number(t?.y ?? 0) * 100), true);
    view.setInt16(o + 6, Math.round(Number(t?.angle ?? 0) * 1000), true);
    const h = e.traits.Health;
    view.setUint16(o + 8, Math.max(0, Math.round(Number(h?.hp ?? 0))), true);
    const p = e.traits.Player;
    view.setUint8(o + 10, Number(p?.slot ?? 255));
    o += 16;
  }
  return new Uint8Array(buf, 0, o);
}

export function snapshotBytes(snap: WorldSnapshot): number {
  return encodeSnapshotBinary(snap).byteLength;
}

export function hostContentMessages(settingsJson: string, levelJson: string): NetMessage[] {
  return [
    { t: 'settings', json: settingsJson },
    { t: 'level', json: levelJson },
  ];
}

export function lateJoinSnapshotMessage(snap: WorldSnapshot): NetMessage {
  return { t: 'snapshot', snap };
}

export function quantizeInput(input: PlayerInput): PlayerInput {
  return {
    ...input,
    moveX: Math.round(input.moveX * 64) / 64,
    aimX: Math.round(input.aimX * 64) / 64,
    aimY: Math.round(input.aimY * 64) / 64,
  };
}
